/**
 * WASSER E-commerce Platform - Route Protection Hook
 *
 * Унифицированный hook для проверки доступа к маршрутам.
 * Используется в Client и Admin приложениях.
 *
 * @example
 * const { isAllowed, isLoading, redirectPath } = useRouteProtection({
 *   requireAuth: true,
 *   requireB2B: true,
 * });
 */

/**
 * @typedef {Object} RouteProtectionOptions
 * @property {boolean} [requireAuth=false] - Требуется авторизация
 * @property {boolean} [requireB2B=false] - Требуется B2B доступ
 * @property {boolean} [requireAdmin=false] - Требуется admin роль
 * @property {string} [loginPath='/login'] - Путь для редиректа неавторизованных
 * @property {string} [b2bApplyPath='/b2b/apply'] - Путь для B2B заявки
 * @property {string} [defaultPath='/'] - Путь по умолчанию (для отказа в доступе)
 */

/**
 * @typedef {Object} RouteProtectionResult
 * @property {boolean} isAllowed - Разрешен ли доступ
 * @property {boolean} isLoading - Идет загрузка данных авторизации
 * @property {string|null} redirectPath - Путь для редиректа (если доступ запрещен)
 * @property {string|null} reason - Причина отказа ('not_authenticated' | 'no_b2b_access' | 'not_admin')
 */

/**
 * Hook для проверки доступа к защищенному маршруту
 *
 * @param {Object} authState - Состояние авторизации из контекста
 * @param {boolean} authState.isAuthenticated - Пользователь авторизован
 * @param {boolean} authState.isLoading - Загрузка данных авторизации
 * @param {Function} [authState.hasB2BAccess] - Функция проверки B2B доступа
 * @param {Function} [authState.isAdmin] - Функция проверки admin роли
 * @param {Object} authState.user - Данные пользователя
 * @param {RouteProtectionOptions} options - Опции защиты маршрута
 * @returns {RouteProtectionResult}
 */
export const useRouteProtection = (authState, options = {}) => {
  const {
    requireAuth = false,
    requireB2B = false,
    requireAdmin = false,
    loginPath = '/login',
    b2bApplyPath = '/b2b/apply',
    defaultPath = '/',
  } = options;

  const {
    isAuthenticated = false,
    isLoading = false,
    hasB2BAccess,
    isAdmin,
    user,
  } = authState;

  // Если идет загрузка - возвращаем loading состояние
  if (isLoading) {
    return {
      isAllowed: false,
      isLoading: true,
      redirectPath: null,
      reason: null,
    };
  }

  // Проверка авторизации
  if (requireAuth && !isAuthenticated) {
    return {
      isAllowed: false,
      isLoading: false,
      redirectPath: loginPath,
      reason: 'not_authenticated',
    };
  }

  // Проверка B2B доступа
  if (requireB2B) {
    const hasB2B =
      typeof hasB2BAccess === 'function'
        ? hasB2BAccess()
        : user?.b2bStatus === 'approved';

    if (!hasB2B) {
      return {
        isAllowed: false,
        isLoading: false,
        redirectPath: b2bApplyPath,
        reason: 'no_b2b_access',
      };
    }
  }

  // Проверка admin роли
  if (requireAdmin) {
    const isAdminUser =
      typeof isAdmin === 'function' ? isAdmin() : user?.role === 'admin';

    if (!isAdminUser) {
      return {
        isAllowed: false,
        isLoading: false,
        redirectPath: defaultPath,
        reason: 'not_admin',
      };
    }
  }

  // Доступ разрешен
  return {
    isAllowed: true,
    isLoading: false,
    redirectPath: null,
    reason: null,
  };
};

/**
 * Проверяет, имеет ли пользователь указанную роль
 * @param {Object} user - Объект пользователя
 * @param {string|string[]} requiredRoles - Требуемая роль или массив ролей
 * @returns {boolean}
 */
export const hasRole = (user, requiredRoles) => {
  if (!user?.role) return false;

  const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
  return roles.includes(user.role);
};

/**
 * Проверяет, имеет ли пользователь указанное разрешение
 * @param {Object} user - Объект пользователя
 * @param {string} permission - Требуемое разрешение
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
  if (!user?.permissions) return false;
  return user.permissions.includes(permission);
};

export default useRouteProtection;
